import { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import type { WorkspaceTime } from '../lib/api';
import { EmptyState } from './ui';

// ─── Logged time per week, with the unbilled portion emphasised ─────────────
//
// The reader has ONE question: when did unbilled work pile up, and is it still
// piling up? So unbilled hours are the subject — one emphasised mark, anchored
// to the baseline where it is easiest to compare — and the rest of the week's
// logged time sits above it as a recessive neutral for context.
//
// It is deliberately NOT a four-segment stack of the four worklog states. The
// app's status tokens cannot carry that: green-vs-red is deltaE 4.4 under
// deuteranopia in light mode, and amber-vs-red is 14.5 for NORMAL vision in
// dark — both below the readable floor, both verified rather than guessed. The
// budget timeline hit the same wall and resolved it the same way. Per-state
// detail is carried by the tiles and the table beside this chart, in text.
//
// Two marks, so a legend is always present; the recessive fill sits below 3:1
// against the surface, which obliges visible relief — the by-week table in the
// same section is it.

interface Row {
  week_start: string;
  unbilled: number;
  rest: number;
  logged: number;
  needs_review: number;
  billed: number;
  non_billable: number;
}

const fmtWeek = (s: string): string => s.slice(5);   // MM-DD; the year is on the range

export function WeeklyTimeChart({ time }: { time: WorkspaceTime }) {
  const rows = useMemo<Row[]>(
    () => time.by_week.map((w) => ({
      week_start: w.week_start,
      unbilled: w.unbilled_hours,
      // The remainder, not a second measure: unbilled + rest === logged, so the
      // column height is always the week's total and the red portion reads as
      // a share of it.
      rest: Math.max(0, Math.round((w.hours - w.unbilled_hours) * 100) / 100),
      logged: w.hours,
      needs_review: w.needs_review_hours,
      billed: w.billed_hours,
      non_billable: w.non_billable_hours,
    })),
    [time],
  );

  if (rows.length === 0) return <EmptyState message="No time logged yet." />;

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2 }}>Logged time by week</div>
      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 6 }}>
        Column height is the week’s logged hours; the red portion is what is still unbilled.
      </div>
      <div style={{ height: 220 }}>
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 4 }} barCategoryGap="18%">
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="week_start"
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              tickFormatter={fmtWeek}
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              width={44}
              tickFormatter={(v) => `${v}h`}
            />
            <Tooltip content={<WeekTooltip />} cursor={{ fill: 'var(--color-border)' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="square" iconSize={9} />
            {/* Unbilled first so it stacks at the baseline — the emphasised
                mark belongs where lengths are easiest to compare. The 2px
                stroke in the surface colour is the gap between segments. */}
            <Bar
              dataKey="unbilled" name="Unbilled" stackId="t"
              fill="var(--chart-unbilled)"
              stroke="var(--color-surface)" strokeWidth={2}
            />
            <Bar
              dataKey="rest" name="Billed, review & non-billable" stackId="t"
              fill="var(--chart-time-rest)"
              stroke="var(--color-surface)" strokeWidth={2}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Full per-state breakdown on hover — where the four states are readable,
 *  because here they are labelled text rather than four competing fills. */
function WeekTooltip({ active, payload, label }: {
  active?: boolean; payload?: Array<{ payload: Row }>; label?: string;
}) {
  if (!active || !payload?.length) return null;
  const r = payload[0]!.payload;
  const line = (k: string, v: number, emphasis?: boolean) => v > 0 && (
    <div key={k} style={{ display: 'flex', gap: 12, justifyContent: 'space-between', color: emphasis ? 'var(--color-red-text)' : undefined }}>
      <span>{k}</span><span>{v.toFixed(2)}h</span>
    </div>
  );
  return (
    <div style={{
      background: 'var(--color-surface)', border: '0.5px solid var(--color-border-strong)',
      borderRadius: 'var(--radius-sm)', padding: '8px 10px', fontSize: 11, minWidth: 190,
    }}>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>Week of {label}</div>
      {line('Unbilled', r.unbilled, true)}
      {line('Needs review', r.needs_review)}
      {line('Billed', r.billed)}
      {line('Non-billable', r.non_billable)}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 4, paddingTop: 4, borderTop: '0.5px solid var(--color-border)' }}>
        <span style={{ color: 'var(--color-text-muted)' }}>Logged</span>
        <span>{r.logged.toFixed(2)}h</span>
      </div>
    </div>
  );
}
